import React, { FC, useEffect, useMemo, useState } from 'react'
import { FieldArray, Field, FormikProps } from 'formik'
import { blockInvalidChar, blockInvalidCharWithDecimal, KTIcon, ensureNonNegative } from 'src/marketConnect/helpers'
import SelectProductImageModal from './SelectedProductImage'
import { useLocation, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faPenToSquare, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons'
import { useAppDispatch } from 'src/redux/storeHooks'
import { CreateEditState } from './old-CreateProduct'
import { toast } from 'react-toastify'
import CommonAlertModal from 'src/utils/modals/CommonAlertModal'
import { deleteVariantAsync } from 'src/redux/actions/omsProductActions'
import { getApiErrorMessage } from 'src/marketConnect/helpers'
const MAX_OPTIONS = 3

interface VariantComponentProps {
  values: any
  setFormikState: FormikProps<any>['setFormikState']
  mode: 'create' | 'edit'
  productId?: number
  editInitialValues: any
  productRowData: any
  currentPlatformId: Number | undefined
}

export function getDefaultTrackedState(existingVariants: any[], mainProductIsTracked?: boolean): boolean {
  if (existingVariants && existingVariants.length > 0) {
    return Boolean(existingVariants[existingVariants.length - 1]?.isTracked);
  }
  if (mainProductIsTracked !== undefined) {
    return Boolean(mainProductIsTracked);
  }
  return true; // Absolute fallback
}
// ─── Pure Helpers ───

const getTotalInventoryQty = (inv: any[]) =>
  (inv || []).reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0)

const toInputString = (v: any) => (v === null || v === undefined ? '' : String(v))

const normalizeKeyPart = (value: any) =>
  String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '')

const getOptionIdentity = (opt: any, index: number) => {
  const id = opt?.id ?? opt?.platform_product_option
  if (id !== null && id !== undefined && id !== '') return `id:${Number(id)}`
  return `name:${normalizeKeyPart(opt?.name) || index}`
}

const getOptionValueIdentity = (opt: any, value: any, index: number) => {
  const valueText = String(value ?? '').trim()
  const valueIds = opt?.value_ids && typeof opt.value_ids === 'object' ? opt.value_ids : {}
  const directId = valueIds[valueText]
  if (directId !== null && directId !== undefined) return `id:${Number(directId)}`

  const lowerValue = valueText.toLowerCase()
  const matchedOptionValue = (Array.isArray(opt?.option_values) ? opt.option_values : [])
    .find((v: any) => String(v?.value ?? '').trim().toLowerCase() === lowerValue)
  if (matchedOptionValue?.id) return `id:${Number(matchedOptionValue.id)}`

  const matchedValue = (Array.isArray(opt?.values) ? opt.values : [])
    .find((v: any) => typeof v === 'object' && String(v?.value ?? '').trim().toLowerCase() === lowerValue)
  if (matchedValue?.id) return `id:${Number(matchedValue.id)}`

  return `value:${normalizeKeyPart(valueText)}`
}

const optionContainsValue = (opt: any, value: any) => {
  const target = String(value ?? '').trim().toLowerCase()
  if (!target) return false

  const values = [
    ...(Array.isArray(opt?.values) ? opt.values : []),
    ...(Array.isArray(opt?.option_values) ? opt.option_values : []),
  ]

  return values.some((v: any) =>
    String(typeof v === 'object' && v !== null ? v.value : v).trim().toLowerCase() === target
  )
}

const getComboKey = (optionValues: string[] = [], options: any[] = []) => {
  if (!options.length) {
    return [...(optionValues || [])]
      .map((v: string) => `value:${normalizeKeyPart(v)}`)
      .sort()
      .join('|')
  }

  const usedValueIndexes = new Set<number>()

  return options
    .map((opt: any, optIndex: number) => {
      let valueIndex = optIndex
      let value = optionValues?.[optIndex]

      if (!optionContainsValue(opt, value)) {
        const matchedIndex = (optionValues || []).findIndex((candidate: any, candidateIndex: number) =>
          !usedValueIndexes.has(candidateIndex) && optionContainsValue(opt, candidate)
        )

        if (matchedIndex !== -1) {
          valueIndex = matchedIndex
          value = optionValues[matchedIndex]
        }
      }

      usedValueIndexes.add(valueIndex)

      return `${getOptionIdentity(opt, optIndex)}:${getOptionValueIdentity(opt, value, valueIndex)}`
    })
    .sort()
    .join('|')
}

const isSubsetCombination = (sourceValues: string[] = [], targetValues: string[] = []) => {
  const targetSet = new Set((targetValues || []).map((v) => normalizeKeyPart(v)))
  return (sourceValues || [])
    .filter((v) => String(v ?? '').trim())
    .every((v) => targetSet.has(normalizeKeyPart(v)))
}

// supports BOTH create-mode (values: string[]) and edit-mode (option_values: {value:string}[])
const getOptValuesAsStrings = (opt: any): string[] => {
  if (Array.isArray(opt?.values)) return opt.values // create mode
  if (Array.isArray(opt?.option_values)) return opt.option_values.map((x: any) => x.value) // edit mode
  return []
}

const normalizeOptions = (opts: any[]) =>
  (opts || []).map((opt: any) => ({
    ...opt,
    __values: getOptValuesAsStrings(opt)
      .map((v: string) => v.trim())
      .filter(Boolean),
  }))

const parseVariantNameOptionValues = (variantName: any, options: any[]): string[] => {
  if (!variantName || !(options || []).length) return []

  const nameParts = String(variantName)
    .split('/')
    .map((p) => p.trim())
    .filter(Boolean)

  if (!nameParts.length) return []

  const namedValues: string[] = []
  let matchedNamedParts = 0

  ; (options || []).forEach((opt: any) => {
    const optName = String(opt?.name || opt).trim().toLowerCase()
    const matchingPart = nameParts.find((p) => p.toLowerCase().startsWith(`${optName}:`))

    if (matchingPart) {
      const value = matchingPart.substring(matchingPart.indexOf(':') + 1).trim()
      const validValues = getOptValuesAsStrings(opt).map((v) => normalizeKeyPart(v))

      if (!validValues.length || validValues.includes(normalizeKeyPart(value))) {
        matchedNamedParts += 1
        namedValues.push(value)
      }
    }
  })

  if (matchedNamedParts === options.length) return namedValues

  const positionalValues: string[] = []
  const usedPartIndexes = new Set<number>()

  ; (options || []).forEach((opt: any) => {
    const optVals = getOptValuesAsStrings(opt).map((v) => String(v).trim().toLowerCase())
    const matchingIndex = nameParts.findIndex((p, index) =>
      !usedPartIndexes.has(index) && optVals.includes(p.toLowerCase())
    )

    if (matchingIndex !== -1) {
      usedPartIndexes.add(matchingIndex)
      positionalValues.push(nameParts[matchingIndex])
    }
  })

  if (positionalValues.length === options.length) return positionalValues

  if (nameParts.length === options.length) {
    return nameParts.map((part) => {
      const colonIndex = part.indexOf(':')
      return colonIndex !== -1 ? part.substring(colonIndex + 1).trim() : part
    })
  }

  return []
}

const parseOptionValues = (
  variant: any,
  options: any[],
  optionValueIdMap: Record<number, string>
): string[] => {
  const slots = [
    variant?.platform_product_option_value1,
    variant?.platform_product_option_value2,
    variant?.platform_product_option_value3,
  ].filter(Boolean)

  if (slots.length > 0) {
    const slotMap: Record<string, string> = {}
    slots.forEach((slot: any) => {
      const val = typeof slot === 'object' ? (slot.value ?? optionValueIdMap[Number(slot.id)]) : optionValueIdMap[Number(slot)]
      const optName = typeof slot === 'object' ? slot.option_name : null
      if (optName && val) {
        slotMap[String(optName).trim().toLowerCase()] = val
      }
    })

    if (Object.keys(slotMap).length > 0) {
      const mappedValues = (options || []).map((opt: any) => {
        const optName = String(opt?.name || opt).trim().toLowerCase()
        return slotMap[optName] || ""
      })
      if (mappedValues.some(v => v !== "")) {
        return mappedValues
      }
    }

    const slotValues: string[] = []
    slots.forEach((slot: any) => {
       const val = typeof slot === 'object' ? (slot.value ?? optionValueIdMap[Number(slot.id)]) : optionValueIdMap[Number(slot)]
       if (val) slotValues.push(val)
    })
    if (slotValues.length > 0 && slotValues.length === (options || []).length) {
       return slotValues
    }
  }

  const valuesFromName = parseVariantNameOptionValues(variant?.name, options)
  if (valuesFromName.length === (options || []).length) {
    return valuesFromName
  }

  return [variant?.name ?? '']
}

const VariantComponent: FC<VariantComponentProps> = ({
  values,
  setFormikState,
  mode,
  productId,
  editInitialValues,
  productRowData,
  currentPlatformId
}) => {
  console.log("values in variant comp", values)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as CreateEditState | undefined
  const { rowData } = state || { mode: 'create', rowData: undefined }

  const isEditMode = mode === 'edit'

  const [enabled, setEnabled] = useState<boolean>(() => {
    return isEditMode ? Boolean(values?.isProductHasVariant) : false
  })

  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const isEditing = activeIndex !== null

  const [collapsedGroups, setCollapsedGroups] = useState<{ [key: string]: boolean }>({})
  const [imageModalOpen, setImageModalOpen] = useState(false)
  const [imageForVariantIndex, setImageForVariantIndex] = useState<number | null>(null)

  const [showDeleteVariantModal, setShowDeleteVariantModal] = useState(false)
  const [variantToDelete, setVariantToDelete] = useState<any>(null)
  const [isDeletingVariant, setIsDeletingVariant] = useState(false)

  // UI gate:
  // - false => (create mode) show ONLY "Add another option" until Done
  // - true  => show Active Options + Editor 
  const [showOptionsUI, setShowOptionsUI] = useState(false)

  // ─── Derived: does the product ALREADY have options created? ───
  // Check BOTH formik options array AND variant option-value bindings
  // from the raw API response (editInitialValues = getEditData).
  const hasOptionsCreated = useMemo(() => {
    // 1) Check formik options — at least 1 option with at least 1 non-empty value
    const formikOpts = values?.options || [];
    const hasFormikOptions = formikOpts.some((opt: any) => {
      const vals = getOptValuesAsStrings(opt).filter((v: string) => v.trim());
      return String(opt?.name ?? '').trim() && vals.length > 0;
    });
    if (hasFormikOptions) return true;

    // 2) Check the raw API product data for product-level options dict
    const apiOpts = editInitialValues?.options;
    if (apiOpts && typeof apiOpts === 'object') {
      const hasApiOptions = Object.values(apiOpts).some((optObj: any) => {
        const vals = Array.isArray(optObj?.values) ? optObj.values : [];
        return vals.length > 0;
      });
      if (hasApiOptions) return true;
    }

    // 3) Check if any variant has option value bindings (belt-and-suspenders)
    const apiVariants = editInitialValues?.variants || [];
    const hasBindings = apiVariants.some((v: any) => {
      return (
        (v?.platform_product_option_value1 !== null && v?.platform_product_option_value1 !== undefined) ||
        (v?.platform_product_option_value2 !== null && v?.platform_product_option_value2 !== undefined) ||
        (v?.platform_product_option_value3 !== null && v?.platform_product_option_value3 !== undefined)
      );
    });

    return hasBindings;
  }, [values?.options, editInitialValues]);

  // ─── Derived: does the matrix have real variant rows (with option-bound data)? ───
  // Only count variant rows that have multi-part optionValues (not a single fallback name)
  const hasMatrixVariants = useMemo(() => {
    if (!hasOptionsCreated) return false;
    const data = values?.varients_data || [];
    return data.some((v: any) =>
      Array.isArray(v?.optionValues) && v.optionValues.length > 0 &&
      v.optionValues.some((ov: string) => String(ov ?? '').trim())
    );
  }, [hasOptionsCreated, values?.varients_data]);

  // ─── Track whether user has changed options since mount ───
  // Matrix (Section 2.b) only appears when this is true
  const [hasOptionsChanged, setHasOptionsChanged] = useState(false);

  // ─── Ref: has the user triggered handleDone at least once this session? ───
  // Used to distinguish first-ever matrix generation (copy defaults) from re-generation (preserve inputs).
  const matrixGeneratedRef = React.useRef(false);

  // ─── Section 2.a: Existing Variants from API (read-only) ───
  // Hydrated directly from the raw API response, never from formik state.
  // These are DB-persisted variants — only editable via /update-variant route.
  const existingApiVariants = useMemo(() => {
    // Build a lookup map: option_value_id → value string
    // from the product-level options dictionary.
    // Shape: options = { "Color": { values: [{id:42079, value:"RED"}, ...] }, ... }
    const optionValueIdMap: Record<number, string> = {};
    const productOptions = editInitialValues?.options;
    if (productOptions && typeof productOptions === 'object') {
      Object.values(productOptions).forEach((optObj: any) => {
        const vals = Array.isArray(optObj?.values) ? optObj.values : [];
        vals.forEach((val: any) => {
          if (val?.id != null && val?.value != null) {
            optionValueIdMap[Number(val.id)] = String(val.value);
          }
        });
      });
    }

    const rawVariants = editInitialValues?.variants || [];
    return rawVariants
      .filter((v: any) => {
        if (!v?.id) return false; // must have a DB id
        // Hide default/system variants — these are created automatically when a product
        // is created without options. They have NO option value slot bindings at all.
        const hasOptionBinding =
          v?.platform_product_option_value1 != null ||
          v?.platform_product_option_value2 != null ||
          v?.platform_product_option_value3 != null;
        return hasOptionBinding; // only show real option-bound variants
      })
      .map((v: any, i: number) => {
        const optionValues = parseOptionValues(v, values?.options, optionValueIdMap);

        return {
          _index: i,
          variant_id: v.id,
          optionValues,
          name: v?.name ?? '',
          price: String(ensureNonNegative(v?.price?.value ?? '')),
          costPrice: String(ensureNonNegative(v?.cost_price?.value ?? '')),
          available: v?.total_available_quantity || 0,
          sku: v?.sku ?? '',
          barcode: v?.barcode ?? '',
          ean: v?.ean ?? '',
          variant_image: v?.image?.image_url
            ? { imageUrl: v.image.image_url, url: v.image.image_url, id: v.image.id }
            : null,
        };
      });
  }, [editInitialValues, values?.options]);

  // ─── Helper: build a Set of existing combo keys for deduplication ───
  const existingComboKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const ev of existingApiVariants) {
      if (Array.isArray(ev.optionValues) && ev.optionValues.length > 0) {
        keys.add(getComboKey(ev.optionValues, values?.options || []));
      }
    }
    return keys;
  }, [existingApiVariants, values?.options]);

  const optionsString = JSON.stringify(values?.options || []);
  const isFirstRun = React.useRef(true);
  const prevOptionsString = React.useRef(optionsString);

  useEffect(() => {
    if (!enabled) return;
    if (activeIndex !== null) return; // Skip regeneration while option editor is open

    const normalized = normalizeOptions(values?.options || []);
    
    const isValid = normalized.length > 0 && normalized.every((o: any) => 
      String(o?.name ?? "").trim() && 
      (o.__values || []).length > 0
    );

    if (normalized.length === 0) {
      const currentData = values?.varients_data || [];
      const isAlreadyJustSingleRow = currentData.length === 1 && !currentData[0].optionValues;
      
      if (currentData.length > 0 && !isAlreadyJustSingleRow) {
        setFormikState((prev: any) => {
          const firstRow = prev.values.varients_data?.[0] || {};
          return {
            ...prev,
            values: { 
              ...prev.values, 
              varients_data: [{
                 sku: firstRow.sku ?? '',
                 barcode: firstRow.barcode ?? '',
                 ean: firstRow.ean ?? '',
                 price: firstRow.price ?? '',
                 costPrice: firstRow.costPrice ?? '',
                 available: firstRow.available ?? ''
              }] 
            }
          };
        });
      }
      return;
    }

    if (!isValid) return; 

    const existingMatrix = values?.varients_data || [];
    const defaultIsTracked = getDefaultTrackedState(existingMatrix, values?.isTracked);

    let variants = generateVariants(normalized, defaultIsTracked);

    const matrixMap = new Map<string, any>();
    existingMatrix.forEach((ev: any) => {
      if (Array.isArray(ev.optionValues) && ev.optionValues.length > 0) {
        const key = getComboKey(ev.optionValues, normalized);
        const existing = matrixMap.get(key);
        if (!existing) {
          matrixMap.set(key, ev);
        } else {
          const evId = ev.variant_id ? Number(ev.variant_id) : null;
          const extId = existing.variant_id ? Number(existing.variant_id) : null;
          if (evId !== null && (extId === null || evId < extId)) {
            matrixMap.set(key, ev);
          }
        }
      }
    });

    const hasGeneratedMatrixBefore = matrixGeneratedRef.current;
    const inheritedParentKeys = new Set<string>();

    variants = variants.map((v: any) => {
      const key = getComboKey(v.optionValues, normalized);
      const existing = matrixMap.get(key);
      if (existing) {
        return {
          ...v,
          price: existing.price ?? v.price,
          costPrice: existing.costPrice ?? v.costPrice,
          sku: existing.sku ?? v.sku,
          barcode: existing.barcode ?? v.barcode,
          ean: existing.ean ?? v.ean,
          available: existing.available ?? v.available,
          variant_id: existing.variant_id ?? v.variant_id,
          variant_image: existing.variant_image ?? v.variant_image,
          isTracked: existing.isTracked ?? v.isTracked,
          reorder_point: existing.reorder_point ?? v.reorder_point,
          name: existing.name ?? v.name,
        };
      }

      const subsetExisting = existingMatrix.find((ev: any) =>
        Array.isArray(ev.optionValues) &&
        ev.optionValues.length < v.optionValues.length &&
        isSubsetCombination(ev.optionValues, v.optionValues)
      );

      if (subsetExisting) {
        const parentKey = [...(subsetExisting.optionValues || [])]
          .map(val => String(val ?? '').trim().toLowerCase())
          .sort()
          .join('|');

        if (!inheritedParentKeys.has(parentKey)) {
          inheritedParentKeys.add(parentKey);
          return {
            ...v,
            variant_id: subsetExisting.variant_id ?? v.variant_id,
            price: subsetExisting.price ?? v.price,
            costPrice: subsetExisting.costPrice ?? v.costPrice,
            sku: subsetExisting.sku ?? v.sku,
            barcode: subsetExisting.barcode ?? v.barcode,
            ean: subsetExisting.ean ?? v.ean,
            available: subsetExisting.available ?? v.available,
            variant_image: subsetExisting.variant_image ?? v.variant_image,
            isTracked: subsetExisting.isTracked ?? v.isTracked,
            reorder_point: subsetExisting.reorder_point ?? v.reorder_point,
          };
        }
      }

      return v;
    });

    if (variants.length > 0) {
      const dbVariants = editInitialValues?.variants || [];
      const isSingleProductConversion = isEditMode ? (dbVariants.length === 1) : true;
      
      if (isSingleProductConversion) {
        if (isEditMode) {
          const baseVariant = dbVariants[0];
          const baseVariantId = baseVariant?.id;
          const isIdAssigned = variants.some((v: any) => v.variant_id === baseVariantId);
          
          if (!isIdAssigned && baseVariantId) {
            const oldRowWithId = existingMatrix.find((ev: any) => ev.variant_id === baseVariantId);
            
            variants[0] = {
              ...variants[0],
              variant_id: baseVariantId,
              price: oldRowWithId?.price ?? toInputString(ensureNonNegative(baseVariant.price?.value ?? baseVariant.price ?? values?.salePrice ?? '')),
              costPrice: oldRowWithId?.costPrice ?? toInputString(ensureNonNegative(baseVariant.cost_price?.value ?? baseVariant.costPrice ?? values?.costPrice ?? '')),
              sku: oldRowWithId?.sku ?? toInputString(baseVariant.sku ?? ''),
              barcode: oldRowWithId?.barcode ?? toInputString(baseVariant.barcode ?? ''),
              ean: oldRowWithId?.ean ?? toInputString(baseVariant.ean ?? ''),
              available: oldRowWithId?.available ?? toInputString(baseVariant.total_available_quantity ?? baseVariant.available ?? ''),
              variant_image: oldRowWithId?.variant_image ?? variants[0].variant_image,
            };
        }
      } else {
          const key = getComboKey(variants[0].optionValues, normalized);
          if (!matrixMap.has(key)) {
            const oldFirstRow = existingMatrix[0];
            if (!hasGeneratedMatrixBefore) {
               const singleProductRow = oldFirstRow || {};
               variants[0] = {
                 ...variants[0],
                 price: toInputString(values?.salePrice),
                 costPrice: toInputString(values?.costPrice),
                 available: toInputString(getTotalInventoryQty(values?.inventory)),
                 sku: singleProductRow.sku ?? '',
                 barcode: singleProductRow.barcode ?? '',
                 ean: singleProductRow.ean ?? '',
               };
               // Intentionally left blank: do NOT seed price/costPrice to subsequent variants
            } else if (oldFirstRow) {
               const oldKey = getComboKey(oldFirstRow.optionValues, normalized);
               const isOldFirstRowStillPresent = variants.some((v: any) => getComboKey(v.optionValues, normalized) === oldKey);
               
               if (!isOldFirstRowStillPresent) {
                 variants[0] = {
                   ...variants[0],
                   price: oldFirstRow.price,
                   costPrice: oldFirstRow.costPrice,
                   sku: oldFirstRow.sku,
                   barcode: oldFirstRow.barcode,
                   ean: oldFirstRow.ean,
                   available: oldFirstRow.available,
                   variant_image: oldFirstRow.variant_image,
                 };
               }
            }
          }
        }
      }
    }

    // Sort: variants with variant_id at the top, new ones (without variant_id) at the bottom
    variants.sort((a: any, b: any) => {
      const aHasId = a.variant_id !== undefined && a.variant_id !== null;
      const bHasId = b.variant_id !== undefined && b.variant_id !== null;
      if (aHasId && !bHasId) return -1;
      if (!aHasId && bHasId) return 1;
      return 0;
    });

    // Re-index _index to match sorted positions and forcefully sync 'available' inventory ONLY for the first variant
    const currentTotalInventory = toInputString(getTotalInventoryQty(values?.inventory));
    variants = variants.map((v: any, idx: number) => ({ 
      ...v, 
      _index: idx,
      available: v.variant_id ? v.available : (idx === 0 ? currentTotalInventory : '')
    }));

    const currentKeys = existingMatrix.map((v: any) => getComboKey(v.optionValues, normalized)).join(',');
    const newKeys = variants.map((v: any) => getComboKey(v.optionValues, normalized)).join(',');
    
    const optionsDidActuallyChange = prevOptionsString.current !== optionsString;
    prevOptionsString.current = optionsString;
    const isUserInteraction = !isFirstRun.current && optionsDidActuallyChange;

    // Detect if 'available' has drifted from the current product-level inventory (only check the first variant)
    const inventoryChanged = existingMatrix.length === variants.length && variants.length > 0 && (() => {
      const ev = existingMatrix[0];
      const nv = variants[0];
      if (!nv || nv.variant_id) return false;
      return ev.available !== nv.available;
    })();

    if (currentKeys !== newKeys || inventoryChanged) {
       // Only inject new variants into formik if the user has explicitly interacted
       // or the matrix is already revealed. This prevents ghost variants on mount.
       if (isUserInteraction || hasOptionsChanged || inventoryChanged) {
           matrixGeneratedRef.current = true;
           setFormikState((prev: any) => ({
             ...prev,
             values: { ...prev.values, varients_data: variants }
           }));
           setHasOptionsChanged(true);
       }
    }

    isFirstRun.current = false;
  }, [optionsString, enabled, existingComboKeys, isEditMode, editInitialValues, values?.salePrice, values?.costPrice, values?.inventory, setFormikState, hasOptionsChanged]);


  useEffect(() => {
    // Edit mode: if product has variants and options exist, show chips automatically
    if (isEditMode && enabled && hasOptionsCreated) {
      setShowOptionsUI(true);
    }
  }, [mode, enabled, hasOptionsCreated]);

  useEffect(() => {
    // Variant products should not be treated as physical single-product inventory items
    setEnabled(values?.isProductHasVariant)
  }, [mode, values?.isProductHasVariant])

  // useEffect(() => {
  //   if (isEditMode) { 
  //     setEnabled(Boolean(values?.isProductHasVariant))
  //   } else {
  //     setEnabled(false)
  //   } 
  // }, [mode, values?.isProductHasVariant]) 

  // keep local enabled in sync with formik
  useEffect(() => {
    if (values?.isProductHasVariant) {
      setEnabled(true)
    } else {
      setEnabled(false)
      setShowOptionsUI(false)
      setActiveIndex(null)
    }
  }, [values?.isProductHasVariant])



  const setVariantImage = (variantIndex: number, img: any) => {
    setFormikState((prev) => {
      const copy = [...(prev.values.varients_data || [])]
      const idx = copy.findIndex((v: any) => v._index === variantIndex)
      if (idx === -1) return prev

      copy[idx] = {
        ...copy[idx],
        variant_image: img ? {
          fileName: img.fileName,
          imageUrl: img.url || img.imageUrl,
        } : null,
      }

      return {
        ...prev,
        values: {
          ...prev.values,
          varients_data: copy,
        },
      }
    })
  }

  const getVariantArrayIndex = (variantInternalIndex: number) => {
    return (values?.varients_data || []).findIndex((v: any, i: number) => {
      const idx = v?._index ?? i
      return idx === variantInternalIndex
    })
  }

  const goToVariantEditor = (variantInternalIndex: number, variantId?: any) => {
    // Ensure we have a valid numeric platform ID
    const platformId = Number(currentPlatformId);
    const prodId = Number(productId);
    const varId = Number(variantId);

    if (!platformId || isNaN(platformId) || !prodId || isNaN(prodId) || !varId || isNaN(varId)) {
      toast.error('Platform, Product or Variant ID is missing or invalid');
      return;
    }

    const arrayIndex = getVariantArrayIndex(variantInternalIndex)
    navigate(`/update-variant/platform/${platformId}/productId/${prodId}/variantId/${varId}`, {
      state: {
        mode: 'edit',
        productId: prodId,
        platformId: platformId,
        formikData: values,
        getEditData: editInitialValues,
        activeVariantIndex: arrayIndex >= 0 ? arrayIndex : 0,
        variantId: varId,
      },
    })
  }

  const goToAddVariant = () => {
    const platformId = Number(currentPlatformId);
    const prodId = Number(productId);

    if (!platformId || isNaN(platformId) || !prodId || isNaN(prodId)) {
      toast.error('Platform or Product ID is missing or invalid');
      return;
    }

    // Map current formik values.options (array) to the API options structure (object)
    const apiOptionsObj: Record<string, any> = {};
    if (Array.isArray(values?.options)) {
      values.options.forEach((opt: any) => {
        if (!opt || !opt.name) return;
        const optName = String(opt.name).trim();
        const valuesArr = Array.isArray(opt.values) ? opt.values : [];
        
        const mappedValues = valuesArr.map((val: string) => {
          const trimmedVal = String(val).trim();
          const valId = opt.value_ids?.[trimmedVal] ?? null;
          return {
            ...(valId != null ? { id: Number(valId) } : {}),
            value: trimmedVal,
          };
        });

        apiOptionsObj[optName] = {
          ...(opt.id ? { id: Number(opt.id) } : {}),
          ...(opt.position !== undefined ? { position: Number(opt.position) } : {}),
          values: mappedValues,
        };
      });
    }

    // Strip variant option slots so getOrderedOptionDefs skips fallback 1
    // and uses our mapped options dictionary (fallback 3) instead.
    // All other variant fields (id, name, image, price, etc.) are preserved.
    const sanitizedVariants = (editInitialValues?.variants || []).map((v: any) => ({
      ...v,
      platform_product_option_value1: null,
      platform_product_option_value2: null,
      platform_product_option_value3: null,
    }));

    const nextGetEditData = {
      ...(editInitialValues || {}),
      options: apiOptionsObj,
      variants: sanitizedVariants,
    };

    navigate(`/variant-create/platform/${platformId}/productId/${prodId}`, {
      state: {
        mode: 'add',
        productId: prodId,
        platformId: platformId,
        formikData: values,
        getEditData: nextGetEditData,
        activeVariantIndex: values?.varients_data?.length || 0,
      },
    })
  }



  const generateVariants = (options: any[], defaultIsTracked: boolean = true) => {
    const cartesian = (arr: string[][]): string[][] =>
      arr.reduce<string[][]>((a, b) => a.flatMap((d) => b.map((e) => [...d, e])), [[]])

    const combos =
      options.length === 1
        ? options[0].__values.map((v: string) => [v])
        : cartesian(options.map((o: any) => o.__values))

    return combos.map((c, i) => ({
      _index: i,
      optionValues: c,
      price: '',
      costPrice: '',
      available: '',
      sku: '',
      barcode: '',
      ean: '',
      reorder_point: '',
      isTracked: defaultIsTracked,
      variant_image: null,
    }))
  }

  const handleDone = () => {
    // Clean up any empty/blank options that are not the currently active index
    const cleanedOptions = (values?.options || []).filter((opt: any, idx: number) => {
      if (idx === activeIndex) return true; // always keep active option for validation
      return !!opt?.name?.trim(); // filter out other options that have empty names
    });

    const normalized = normalizeOptions(cleanedOptions);

    // 1) option name required
    const hasEmptyName = normalized.some((o: any) => !String(o?.name ?? "").trim());
    if (hasEmptyName) {
      toast.error("Please fill the option name");
      return;
    }

    // 2) values required (no empty strings)
    const hasAnyEmptyValue = normalized.some((o: any) =>
      (o.__values || []).some((v: string) => !String(v).trim())
    );
    if (hasAnyEmptyValue) {
      toast.error("Fill the value for variant");
      return;
    }

    // 3) must have at least 1 values per option
    const hasSingleValue = normalized.some((o: any) => (o.__values?.length || 0) < 1);
    if (hasSingleValue) {
      toast.error("Fill the Value for variant");
      return;
    }

    // ✅ if valid, proceed
    const optionsForFormik = normalized.map((o: any, idx: number) => {
      const { option_values, __values, ...rest } = o;
      return {
        ...rest,
        values: o.__values,
        position: Number(o.position ?? idx + 1),
      };
    });

    setFormikState((prev) => ({
      ...prev, values: { ...prev.values, options: optionsForFormik },
    }));
    setActiveIndex(null);

    // Mark that options have been changed — this triggers matrix visibility
    setHasOptionsChanged(true);
  };

  const groupedVariants = useMemo(() => {
    const map: Record<string, any[]> = {}

      ; (values.varients_data || []).forEach((variant: any, index: number) => {
        if (!Array.isArray(variant?.optionValues) || variant.optionValues.length === 0) return

        const groupKey = String(variant.optionValues[0] ?? '')
        if (!map[groupKey]) map[groupKey] = []

        map[groupKey].push({
          ...variant,
          _index: variant._index ?? index,
          variant_id: variant?.variant_id,
        })
      })

    return map
  }, [values.varients_data])

  const updateVariantField = (variantIndex: number, field: string, value: string) => {
    setFormikState((prev) => {
      const copy = [...(prev.values.varients_data || [])]
      const idx = copy.findIndex((v: any) => v._index === variantIndex)
      if (idx === -1) return prev
      copy[idx] = { ...copy[idx], [field]: value }
      return { ...prev, values: { ...prev.values, varients_data: copy } }
    })
  }

  const handleDeleteVariantConfirm = async () => {
    if (!variantToDelete || !productId) return
    try {
      setIsDeletingVariant(true)
      const res: any = await dispatch(
        deleteVariantAsync({
          productId: Number(productId),
          variantId: variantToDelete.variant_id,
        })
      ).unwrap()

      const status = Number(res?.status)
      if (status === 200 || status === 201) {
        toast.success(res?.message || 'Variant deleted successfully')
        window.location.reload()
        
        // Remove from formik state
        setFormikState((prev) => {
          const currentVarients = [...(prev.values.varients_data || [])]
          const idx = currentVarients.findIndex((v: any) => v._index === variantToDelete._index)
          if (idx !== -1) {
             currentVarients[idx] = {
               ...currentVarients[idx],
               variant_id: undefined,
               price: '',
               costPrice: '',
               sku: '',
               barcode: '',
               ean: '',
               available: '',
               variant_image: null,
             }
          }
          return {
            ...prev,
            values: { ...prev.values, varients_data: currentVarients }
          }
        })
        
        // Remove from editInitialValues to prevent it from respawning
        if (editInitialValues && Array.isArray(editInitialValues.variants)) {
           const dbIdx = editInitialValues.variants.findIndex((v: any) => v.id === variantToDelete.variant_id)
           if (dbIdx !== -1) {
             editInitialValues.variants.splice(dbIdx, 1)
           }
        }
      } else {
        toast.error(res?.message || res?.data?.message || res?.errors?.detail || 'Failed to delete variant')
      }
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Failed to delete variant'))
    } finally {
      setIsDeletingVariant(false)
      setShowDeleteVariantModal(false)
      setVariantToDelete(null)
    }
  }

  const toggleGroup = (group: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }))
  }

  const variantsCount = (values?.varients_data || []).length

  const hasValidFormikOptions = (values?.options || []).some((opt: any) => {
    const name = String(opt?.name ?? '').trim();
    const vals = getOptValuesAsStrings(opt).filter((v: string) => v.trim());
    return name && vals.length > 0;
  }) && (values?.options || [])
    .filter((opt: any) => String(opt?.name ?? '').trim())
    .every((opt: any) => !!opt?.id);

  console.log("values in variant comp", values?.isProductHasVariant, enabled)

  return (
    <>
      <div className="card card-flush">
        {/* Card header */}
        <div className="card-headerP d-flex justify-content-between align-items-center">
          <div className="card-titleP">
            <h2 className="mb-0">Variants & Options</h2>
          </div>
 
          <div className="d-flex align-items-center gap-2">
            <label
              className="form-check-label text-muted h4 mb-0" 
              htmlFor="variantToggle"
              style={{ fontSize: '13px' }}
            >
              This product has variants
            </label>

            <div className="form-check form-switch m-0">
              <input
                className="form-check-input h-20px w-40px"
                type="checkbox"
                id="variantToggle"
                checked={enabled}
                onChange={(e) => {
                  const checked = e.target.checked
                  setEnabled(checked)
                  setFormikState((prev: any) => {
                    const firstVariant = prev.values.varients_data?.[0];
                    return {
                      ...prev,
                      values: {
                        ...prev.values,
                        isProductHasVariant: checked,
                        ...( !checked && firstVariant?.price !== undefined
                            ? { salePrice: firstVariant.price } 
                            : {} ),
                        ...( !checked && firstVariant?.costPrice !== undefined
                            ? { costPrice: firstVariant.costPrice }
                            : {} ),
                      },
                    };
                  });

                  if (checked) {
                    // If no options exist yet, immediately open the options editor
                    // so user is guided to add options first
                    setActiveIndex(null)
                    setShowOptionsUI(false)
                  }
                  // We removed the else block that cleared options and varients_data
                  // to persist active options and matrix table when toggled off.
                }}
              />
            </div>
          </div>
        </div> 

        {/* Card body */}
        {enabled && (
          <div className="card-bodyP pt-0 mt-2">
            {/* When no options have been created yet, show a prompt + add option button */}
            {!hasOptionsCreated && !showOptionsUI && (
              <div className="border rounded p-4 text-center" style={{ background: '#f8fafc' }}>
                <p className="text-muted mb-3" style={{ fontSize: 14 }}>
                  Add options (like Size, Color) to create variant combinations for this product.
                </p>
                <button
                  type="button"
                  className="btn btn-light variant-btn-table"
                  style={{
                    color: '#2563EB',
                    border: '1px solid #2563EB',
                    padding: '6px !important',
                  }}
                  onClick={() => {
                    setShowOptionsUI(true)

                    setFormikState((prev) => {
                      const nextOptions = [...(prev.values.options || [])]
                      if (nextOptions.length >= MAX_OPTIONS) return prev

                      const newIndex = nextOptions.length
                      nextOptions.push({ name: '', values: [''], position: newIndex + 1 })

                      setActiveIndex(newIndex)

                      return {
                        ...prev,
                        values: { ...prev.values, options: nextOptions },
                      }
                    })
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add Option
                </button>
              </div>
            )}

            {/* When options already exist but options UI is collapsed, show the "Add option" entry point */}
            {hasOptionsCreated && !showOptionsUI && (
              <button
                type="button"
                className="btn btn-light variant-btn-table"
                style={{
                  color: '#2563EB',
                  border: '1px solid #2563EB',
                  margin: '5px',
                  padding: '6px !important',
                }}
                onClick={() => {
                  setShowOptionsUI(true)

                  setFormikState((prev) => {
                    const nextOptions = [...(prev.values.options || [])]
                    if (nextOptions.length >= MAX_OPTIONS) return prev

                    const newIndex = nextOptions.length
                    nextOptions.push({ name: '', values: [''], position: newIndex + 1 })

                    setActiveIndex(newIndex)

                    return {
                      ...prev,
                      values: { ...prev.values, options: nextOptions },
                    }
                  })
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add another option
              </button>
            )}
 
            {showOptionsUI && (
              <>
                <div className="border rounded">
                  <div className="card-titleP p-3 border-bottom">
                    <h2 className="mb-0">Active Options</h2>
                  </div>

                  {(values.options || []).map((opt: any, i: number) => {
                    if (!opt?.name?.trim()) return null;
                    const chips = getOptValuesAsStrings(opt)

                    return (
                      <div
                        key={i}
                        className="d-flex align-items-center justify-content-between px-3 py-3 border-bottom"
                        style={{ gap: 16 }}
                      >
                        <div style={{ minWidth: 120, fontWeight: 700 }}>
                          {opt?.name?.trim() ? opt.name : '—'}
                        </div>

                        <div className="d-flex gap-2 flex-wrap" style={{ flex: 1 }}> 
                          {chips.length ? (
                            chips.map((v: string, vi: number) => (
                              <span key={vi} className="badge bg-light text-dark px-3 py-2">
                                {v}
                              </span>
                            ))
                          ) : (
                            <span className="text-muted" style={{ fontSize: 13 }}>
                              No values
                            </span>
                          )}
                        </div>

                        <button
                          type="button"
                          title="Edit Option"
                          className="btn"
                          onClick={() => setActiveIndex(i)}
                          style={{ minWidth: 30 }}
                        >
                          <FontAwesomeIcon icon={faPenToSquare} />
                        </button>

                        <button
                          type="button"
                          title="Delete Option"
                          className="btn"
                          onClick={() => {
                            const newOptions = [...(values.options || [])]
                            newOptions.splice(i, 1)
                            const resequenced = newOptions.map((o: any, idx: number) => ({
                              ...o,
                              position: idx + 1,
                            }))

                            setFormikState((prev) => ({
                              ...prev,
                              values: { ...prev.values, options: resequenced },
                            }))
                          }}
                          style={{
                            height: 44,
                            minWidth: 44,
                            color: '#dc3545',
                            border: '1px solid #dbe3f0',
                            background: '#fff',
                            borderRadius: 12,
                          }}
                        >
                          <FontAwesomeIcon style={{ color: 'red' }} icon={faTrashCan} />
                        </button>
                      </div>
                    )
                  })}

                  {!isEditing && (values.options?.length || 0) < MAX_OPTIONS && (
                    <button
                      type="button"
                      style={{
                        color: '#2563EB',
                        border: '1px solid #2563EB',
                        margin: '5px', 
                        padding: '6px !important',
                      }}
                      title="Add Option"
                      className="btn btn-light variant-btn-table"
                      onClick={() => {
                        setFormikState((prev) => {
                          const nextOptions = [...(prev.values.options || [])]
                          if (nextOptions.length >= MAX_OPTIONS) return prev

                          const newIndex = nextOptions.length
                          nextOptions.push({ name: '', values: [''] })

                          setActiveIndex(newIndex)
 
                          return {
                            ...prev,
                            values: {
                              ...prev.values,
                              options: nextOptions,
                            },
                          }
                        })
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-2" />
                      Add another option
                    </button>
                  )}
                </div>
 
                {isEditing && (
                  <div className="mt-4">
                    <FieldArray name="options">
                      {() => { 
                        const option = values.options[activeIndex!]
                        const optionValues = option?.values ?? getOptValuesAsStrings(option)

                        return (
                          <div className="border rounded p-4" style={{ background: '#fff' }}>
                            <div className="mb-3">
                              <label className="form-label d-block mb-2">Option name</label>
                              <div className="d-flex align-items-center gap-2">
                                <div style={{ width: '24rem', maxWidth: '100%' }}>
                                  <Field
                                    name={`options.${activeIndex}.name`}
                                    placeholder="Option name (e.g. Size)"
                                    className="form-control"
                                  /> 
                                </div>

                                <div
                                  className="d-flex align-items-center gap-2"
                                  style={{ flex: '0 0 auto' }}
                                >
                                  <button
                                    type="button"
                                    title="Delete option"
                                    className="btn btn-light d-flex align-items-center justify-content-center"
                                    onClick={() => {
                                      const nextOptions = (values.options || []).filter(
                                        (_: any, i: number) => i !== activeIndex
                                      )

                                      const cleaned = normalizeOptions(nextOptions).map((opt: any) => ({
                                        ...opt,
                                        values: opt.__values,
                                      }))

                                      setFormikState((prev) => ({
                                        ...prev, 
                                        values: {
                                          ...prev.values,
                                          options: cleaned,
                                        },
                                      }))

                                      setActiveIndex(null)
                                    }} 
                                    style={{
                                      height: 44,
                                      width: 44,
                                      color: '#dc3545',
                                      border: '1px solid #dbe3f0',
                                      background: '#fff',
                                      borderRadius: 12,
                                      padding: 0,
                                    }}
                                  >
                                    <FontAwesomeIcon 
                                      icon={faTrashCan} 
                                      style={{ color: 'red' }}
                                    />
                                  </button>

                                  <button
                                    type="button"
                                    title="Done"
                                    className="btn btn-primary"
                                    onClick={handleDone}
                                    style={{ height: 44, minWidth: 52, borderRadius: 12, color: '#fff !important' }}
                                  >
                                    <FontAwesomeIcon icon={faCheck} /> Done
                                  </button>
                                </div>
                              </div>
                            </div>
 
                            <div className="pt-2" style={{ borderTop: '1px solid #e7eef8' }}>
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <label className="form-label m-0">Values</label> 
                              </div>

                              <FieldArray name={`options.${activeIndex}.values`}>
                                {(valueHelpers) => (
                                  <>
                                    <div className="d-flex flex-column gap-2"> 
                                      {optionValues.map((_: any, vi: number) => (
                                        <div key={vi} className="d-flex align-items-center gap-2">
                                          <div
                                            className="text-muted"
                                            style={{ width: 70, fontSize: 12, fontWeight: 700 }}
                                          >
                                            Value {vi + 1}
                                          </div>

                                          <div style={{ width: "24rem", maxWidth: "100%" }}>
                                            <Field
                                              name={`options.${activeIndex}.values.${vi}`}
                                              placeholder="Value"
                                              className="form-control"
                                            />
                                          </div>

                                          {optionValues.length > 1 && (
                                            <button
                                              type="button"
                                              title="Delete value"
                                              className="btn btn-icon"
                                              onClick={() => valueHelpers.remove(vi)}
                                              style={{
                                                height: 44,
                                                width: 50,
                                                color: '#dc3545',
                                                border: '1px solid #dbe3f0',
                                                background: '#fff',
                                                borderRadius: 12,
                                              }}
                                            >
                                              <FontAwesomeIcon icon={faTrashCan} />
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>

                                    <div className="mt-3">
                                      <button
                                        type="button"
                                        title="Add value"
                                        className="btn btn-light variant-btn-table"
                                        style={{
                                          color: '#2563EB',
                                          border: '1px solid #2563EB',
                                          padding: '6px !important',
                                        }}
                                        onClick={() => valueHelpers.push('')}
                                      >
                                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                                        Add value
                                      </button>
                                    </div>
                                  </>
                                )}
                              </FieldArray>
                            </div>
                          </div>
                        )
                      }}
                    </FieldArray>
                  </div>
                )}
              </>
            )}
                                {/* ═══════════════════════════════════════════════════════════
                UNIFIED VARIANT MATRIX (EDITABLE)
                Source: values.varients_data (formik state)
                Shows both existing database variants and newly generated combinations.
                Existing rows are marked by 'variant_id' and are read-only for 'available' count.
            ═══════════════════════════════════════════════════════════ */}
            {enabled && hasOptionsCreated && variantsCount > 0 && (
              <div className="card mt-3 mb-3">
                <div className="p-3 border-bottom">
                  <div className="card-titleP d-flex align-items-center justify-content-between">
                    <h2 className="mb-0">Variant Matrix</h2>
                    <span className="badge bg-light text-dark">
                      {variantsCount} {variantsCount === 1 ? 'variant' : 'variants'}
                    </span>
                  </div>
                </div>

                <div className="table-responsive" style={{ padding: '10px' }}>
                  <table className="table align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="text-start" style={{ width: 90 }}>Media</th>
                        <th className="text-start" style={{ width: 180 }}>Variant</th>
                        <th className="text-center" style={{ width: 150 }}>
                          <label className="required mb-0">Sale Price</label>
                        </th>
                        <th className="text-center" style={{ width: 150 }}>Cost Price</th>
                        <th className="text-center" style={{ width: 150 }}>Available</th>
                        <th className="text-center" style={{ width: 180 }}>
                          <label className="required mb-0">SKU</label>
                        </th>
                        <th className="text-center" style={{ width: 180 }}>Barcode</th>
                        <th className="text-center" style={{ width: 100 }}>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {(values.varients_data || []).map((row: any) => {
                        const isExisting = Boolean(row.variant_id)

                        return (
                          <tr key={`matrix-${row._index}`}>
                            <td>
                              <button
                                type="button"
                                onClick={() => {
                                  setImageForVariantIndex(row._index)
                                  setImageModalOpen(true)
                                }}
                                className="border rounded d-flex align-items-center justify-content-center"
                                style={{ width: 40, height: 40, background: '#fff' }}
                              >
                                {row.variant_image?.imageUrl ? (
                                  <img
                                    src={row.variant_image.imageUrl}
                                    alt=""
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }}
                                  />
                                ) : (
                                  <KTIcon iconName="picture" className="fs-2" />
                                )}
                              </button>
                            </td>

                            <td>
                              <div className="d-flex flex-column align-items-start gap-1">
                                <span className="fw-semibold">
                                  {Array.isArray(row.optionValues)
                                    ? row.optionValues.join(' / ')
                                    : row.optionValues?.[0] || row.name}
                                </span>
                                {isExisting && row.name && (
                                  <span 
                                    className="badge"
                                    style={{ 
                                      fontSize: '11px', 
                                      padding: '4px 8px', 
                                      letterSpacing: '0.3px',
                                      background: '#f1f1f4',
                                      color: '#5e6278',
                                      border: '1px solid #e4e6ef',
                                      borderRadius: '6px'
                                    }}
                                  >
                                    {row.name}
                                  </span>
                                )}
                              </div>
                            </td>

                            <td>
                              <input
                                className="form-control"
                                type="number"
                                min={0}
                                step="0.01"
                                value={row.price || ''}
                                onKeyDown={blockInvalidCharWithDecimal}
                                onChange={(e) => updateVariantField(row._index, 'price', e.target.value)}
                              />
                            </td>

                            <td>
                              <input
                                className="form-control"
                                type="number"
                                min={0}
                                step="0.01"
                                value={row.costPrice || ''}
                                onKeyDown={blockInvalidCharWithDecimal}
                                onChange={(e) => updateVariantField(row._index, 'costPrice', e.target.value)}
                              />
                            </td>

                            <td>
                              <input
                                className="form-control"
                                type="number"
                                min={0}
                                readOnly={true}
                                value={row.available || 0}
                                onKeyDown={blockInvalidChar}
                                onChange={(e) => updateVariantField(row._index, 'available', e.target.value)}
                                style={{ backgroundColor: '#f5f8fa', color: '#a1a5b7', cursor: 'not-allowed' }}
                              />
                            </td>

                            <td>
                              <input
                                className="form-control"
                                value={row.sku || ''}
                                onChange={(e) => updateVariantField(row._index, 'sku', e.target.value)}
                              />
                            </td>

                            <td>
                              <input
                                className="form-control"
                                type="text"
                                value={row.barcode || ''}
                                onChange={(e) => updateVariantField(row._index, 'barcode', e.target.value)}
                              />
                            </td>

                            <td>
                              {isExisting ? (
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-icon btn-sm btn-light-primary"
                                    title="Edit Variant"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      goToVariantEditor(row._index, row.variant_id)
                                    }}
                                    style={{
                                      border: '1px solid #dbe3f0',
                                      background: '#fff',
                                      borderRadius: 8,
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faPenToSquare} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-icon btn-sm btn-light-danger"
                                    title="Delete Variant"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      setVariantToDelete(row)
                                      setShowDeleteVariantModal(true)
                                    }}
                                    style={{
                                      color: '#dc3545',
                                      border: '1px solid #dbe3f0',
                                      background: '#fff',
                                      borderRadius: 8,
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTrashCan} />
                                  </button>
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Add new variant button (edit mode only, when real variants exist) ── */}
            {enabled && isEditMode && existingApiVariants.length > 0 && hasValidFormikOptions && (
              <div className="mt-2 mb-2" style={{ paddingLeft: 2 }}>
                <button
                  type="button"
                  className="btn btn-light variant-btn-table"
                  style={{
                    color: '#2563EB',
                    border: '1px solid #2563EB',
                    padding: '6px 14px',
                  }}
                  onClick={goToAddVariant}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add new variant
                </button>
              </div>
            )}
          </div>
        )}

        <SelectProductImageModal
          show={imageModalOpen}
          isSingleSelect={true}
          images={[]}
          onClose={() => {
            setImageModalOpen(false)
            setImageForVariantIndex(null)
          }}
          onSelect={(imgs) => {
            if (imageForVariantIndex !== null) {
              setVariantImage(imageForVariantIndex, imgs && imgs.length > 0 ? imgs[0] : null)
            }
            setImageModalOpen(false)
            setImageForVariantIndex(null)
          }}
        />

        <CommonAlertModal
          title={`Are you sure you want to delete this variant?`}
          subtitle='This variant will be deleted permanently.'
          isVisible={showDeleteVariantModal}
          isTermsVisible={false}
          onSuccess={handleDeleteVariantConfirm}
          onCancel={() => {
            if (isDeletingVariant) return
            setShowDeleteVariantModal(false)
            setVariantToDelete(null)
          }}
          successBtnTitle={isDeletingVariant ? 'Deleting...' : 'Yes, Delete'}
          cancelBtnTitle='Cancel'
          successBtnClasses='btn btn-sm btn-danger m-2'
          successDisabled={isDeletingVariant}
        />
      </div>
    </>
  )
}

export default VariantComponent
